import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Zap, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useApi } from '../../hooks/useApi';

const FREE_TRIAL_AI_LIMIT = 10;

// Read cached org profile synchronously to determine plan instantly
const getInitialState = () => {
  const org = window.__orgProfile;
  if (org) {
    const isFree = org.orgPlanSelection === 'free' && !org.paymentBypassEnabled;
    return {
      totalUsed: org.streamApiUsage?.totalRequests || 0,
      countLoaded: true,
      planKnown: true,
      isFreePlan: isFree,
    };
  }
  return { totalUsed: 0, countLoaded: false, planKnown: false, isFreePlan: false };
};

const AIUsageBanner = () => {
  const { mode } = useTheme();
  const isDark = mode === 'dark';
  const api = useApi();
  const navigate = useNavigate();

  const [state, setState] = useState(getInitialState);

  useEffect(() => {
    if (state.planKnown) return;

    let cancelled = false;
    const fetchUsage = async () => {
      try {
        const response = await api.get('/api/v1/billing/profile');
        if (!cancelled && response.data.success) {
          const data = response.data.data;
          const isFree = data.orgPlanSelection === 'free' && !data.paymentBypassEnabled;
          setState({
            totalUsed: data.streamApiUsage?.totalRequests || 0,
            countLoaded: true,
            planKnown: true,
            isFreePlan: isFree,
          });
        }
      } catch {
        if (!cancelled) setState(prev => ({ ...prev, planKnown: true, countLoaded: true }));
      }
    };
    fetchUsage();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for AI credit usage events — instant count update without API call
  useEffect(() => {
    const handleCreditUsed = () => {
      setState(prev => {
        const newTotal = prev.totalUsed + 1;
        // Also update the cached org profile so other instances stay in sync
        if (window.__orgProfile?.streamApiUsage) {
          window.__orgProfile.streamApiUsage.totalRequests = newTotal;
        }
        return { ...prev, totalUsed: newTotal };
      });
    };
    window.addEventListener('ai-credit-used', handleCreditUsed);
    return () => window.removeEventListener('ai-credit-used', handleCreditUsed);
  }, []);

  if (!state.planKnown || !state.isFreePlan) return null;

  const isExhausted = state.countLoaded && state.totalUsed >= FREE_TRIAL_AI_LIMIT;

  return (
    <div className="flex justify-end mb-3">
      <div
        className={`inline-flex items-center gap-2.5 pl-3 pr-1.5 py-1.5 rounded-full shadow-sm ${
          isExhausted
            ? isDark
              ? 'bg-amber-900/30 border border-amber-500/30'
              : 'bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300'
            : isDark
              ? 'bg-purple-500/10 border border-purple-500/25'
              : 'bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200'
        }`}
      >
        <Sparkles size={13} className={
          isExhausted
            ? isDark ? 'text-amber-400' : 'text-amber-500'
            : isDark ? 'text-purple-400' : 'text-purple-500'
        } />
        <span className={`text-[11px] font-semibold tracking-tight ${
          isExhausted
            ? isDark ? 'text-amber-300' : 'text-amber-700'
            : isDark ? 'text-purple-300' : 'text-purple-700'
        }`}>
          {state.countLoaded ? (
            <>{state.totalUsed}/{FREE_TRIAL_AI_LIMIT} Credits used</>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Loader2 size={10} className="animate-spin" />
              <span>—/{FREE_TRIAL_AI_LIMIT} Credits used</span>
            </span>
          )}
        </span>
        <button
          onClick={() => navigate('/dashboard/settings?section=billing')}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition-all duration-150 ${
            isExhausted
              ? '!text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-sm shadow-amber-500/25'
              : '!text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-sm shadow-purple-500/25'
          }`}
        >
          <Zap size={10} />
          Upgrade
        </button>
      </div>
    </div>
  );
};

export default AIUsageBanner;
